import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { chatActions } from 'store';
import { ChatPanel } from './chatPanel';
import { NewMessageModal } from './newMessageModal';
import { getChatName, getChatAvatar, MESSAGE_TYPE, renderTimeAgo } from './constants';
import { useChatSocketListeners } from '../../utils/hooks/chatSocket';
import { FindLoad, Images, useModal, User } from 'utils';
import { GET_CHATS } from '../../store/chat/chat.types';
import { CustomDelete, EditWithIcon, SingleSearch } from '../../components';

const DELETE_CHART_ACTION_TYPE = 'DELETE_CHAT';

export const ChatDrawer = ({ handleClose }) => {
    const dispatch = useDispatch();
    const chats = useSelector((state) => state.chat.chats);
    const activeChat = useSelector((state) => state.chat.activeChat);
    const { userInfo } = User();
    const currentUserId = userInfo?.id;
    const currentUserType = userInfo?.type || 'Staff';
    const [search, setSearch] = useState('');
    const [newMessageOpen, setNewMessageOpen] = useState(false);
    const [openMenuChatId, setOpenMenuChatId] = useState(null);
    const menuRef = useRef(null);
    const chatsLoading = FindLoad(GET_CHATS);
    const { open } = useModal();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuChatId(null);
            }
        };
        if (openMenuChatId) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuChatId]);

    useChatSocketListeners();

    useEffect(() => {
        if (currentUserId) {
            dispatch(chatActions.getChats());
        }
    }, []);

    useEffect(() => {
        return () => {
            dispatch(chatActions.clearActiveChat());
        };
    }, []);

    const handleSelectChat = (chat) => {
        dispatch(chatActions.setActiveChat(chat));
    };

    const handleBack = () => {
        dispatch(chatActions.clearActiveChat());
    };

    const filteredChats = (chats || []).filter((chat) => {
        if (!search) return true;
        const name = getChatName(chat, currentUserId);
        return name.toLowerCase().includes(search.toLowerCase());
    });

    const ChatSkeleton = () => (
        <>
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="chat-skeleton-chat-item">
                    <div className="chat-skeleton-avatar" />
                    <div className="chat-skeleton-content">
                        <div className="chat-skeleton-line chat-skeleton-line-short" />
                        <div className="chat-skeleton-line chat-skeleton-line-full" />
                    </div>
                </div>
            ))}
        </>
    );

    return (
        <div className="chat-drawer-container">
            <div className="chat-drawer-header">
                <h3>Messages</h3>
                <EditWithIcon icon={Images.editDockBlack} type={'button'} handleClick={() => setNewMessageOpen(true)} />
            </div>
            <div className="chat-search-box">
                <SingleSearch
                    className={'chat-search-input'}
                    placeholder={'Search'}
                    setSearchTerm={(val) => setSearch(val)}
                    searchTerm={search}
                />
            </div>

            <div className="chat-list">
                {chatsLoading?.length ? (
                    <ChatSkeleton />
                ) : filteredChats.length === 0 ? (
                    <div className="chat-empty-state">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                            <rect width="48" height="48" rx="24" fill="#F2F4F7" />
                            <path d="M16 20h16M16 24h10" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
                            <rect x="12" y="14" width="24" height="20" rx="4" stroke="#98A2B3" strokeWidth="1.5" />
                        </svg>
                        <p>No conversations yet</p>
                    </div>
                ) : (
                    filteredChats.map((chat) => {
                        const chatName = getChatName(chat, currentUserId);
                        const chatAvatar = getChatAvatar(chat, currentUserId);
                        const initials = chatName
                            ? chatName
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2)
                            : '?';
                        const isActive = activeChat?.chatId === chat.chatId;
                        const lastMsg = chat.lastMessage;
                        const lastMessageText = lastMsg?.text || '';
                        const isSentByMe = lastMsg?.sender?.userId === currentUserId;
                        const isGroupChat = chat.isGroup;
                        const senderName = lastMsg?.sender?.user?.firstName || lastMsg?.sender?.user?.fullName;
                        const senderPrefix = isGroupChat && !isSentByMe && senderName ? `${senderName}: ` : '';
                        const previewText =
                            lastMsg?.type === MESSAGE_TYPE.SYSTEM
                                ? lastMessageText
                                : isSentByMe
                                ? `You: ${lastMessageText}`
                                : `${senderPrefix}${lastMessageText}`;
                        const timeAgo = renderTimeAgo(chat.lastDate || lastMsg?.createdAt);
                        const currentParticipant = chat.participants?.find((p) => p.userId === currentUserId);
                        const isMuted = currentParticipant?.hiddenNotification;

                        return (
                            <div
                                key={chat.chatId}
                                className={`chat-item ${isActive ? 'chat-item-active' : ''}`}
                                onClick={() => handleSelectChat(chat)}
                            >
                                {isGroupChat && !chatAvatar ? (
                                    <div className="chat-group-avatar-stack">
                                        {(chat.participants || [])
                                            .filter((p) => p.userId !== currentUserId)
                                            .slice(0, 3)
                                            .map((p) => {
                                                const pName = p.user ? `${p.user.firstName || ''} ${p.user.lastName || ''}`.trim() : '';
                                                const pInitials = pName
                                                    ? pName
                                                          .split(' ')
                                                          .map((n) => n[0])
                                                          .join('')
                                                          .toUpperCase()
                                                          .slice(0, 2)
                                                    : '?';
                                                return p.user?.avatar ? (
                                                    <img
                                                        key={p.userId}
                                                        src={p.user.avatar}
                                                        alt={pName}
                                                        className="chat-group-avatar-item"
                                                    />
                                                ) : (
                                                    <div key={p.userId} className="chat-group-avatar-item">
                                                        {pInitials}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ) : chatAvatar ? (
                                    <img src={chatAvatar} alt={chatName} className="chat-avatar" />
                                ) : (
                                    <div className="chat-avatar">{initials}</div>
                                )}

                                <div className="chat-item-content">
                                    <div className="chat-item-top">
                                        <div className="chat-name">
                                            <p>{chatName}</p>

                                            {isMuted && (
                                                <span className="chat-mute-icon">
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path
                                                            d="M10.5 5.25a3.5 3.5 0 10-7 0c0 4.08-1.75 5.25-1.75 5.25h10.5S10.5 9.33 10.5 5.25M8.01 12.25a1.17 1.17 0 01-2.02 0"
                                                            stroke="currentColor"
                                                            strokeWidth="1.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <path
                                                            d="M1 1l12 12"
                                                            stroke="currentColor"
                                                            strokeWidth="1.2"
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>
                                                </span>
                                            )}
                                        </div>

                                        <div className="chat-time-row">
                                            {chat.unread > 0 && <div className="chat-unread-dot" />}
                                            <span className="chat-time">{timeAgo}</span>
                                        </div>
                                    </div>
                                    <div className="chat-preview">
                                        {previewText.length > 50 ? previewText.substring(0, 50) + '...' : previewText}
                                    </div>
                                </div>

                                <div style={{ position: 'relative' }} ref={openMenuChatId === chat.chatId ? menuRef : null}>
                                    <button
                                        className={`chat-item-menu ${openMenuChatId === chat.chatId ? 'chat-item-menu-open' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenMenuChatId(openMenuChatId === chat.chatId ? null : chat.chatId);
                                        }}
                                        title="More"
                                    >
                                        <img src={Images.moreGrayIcon} alt="more" />
                                    </button>
                                    {openMenuChatId === chat.chatId && (
                                        <div className="chat-kebab-menu">
                                            <button
                                                className="chat-kebab-menu-item"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuChatId(null);
                                                    const isMutedNow = isMuted;
                                                    dispatch(chatActions.muteChat(chat.chatId, !isMutedNow));
                                                }}
                                            >
                                                {isMuted ? 'Unmute' : 'Mute'}
                                            </button>

                                            <button
                                                className="chat-kebab-menu-item chat-kebab-menu-item-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuChatId(null);
                                                    open(
                                                        <CustomDelete
                                                            info="Delete Conversation ?"
                                                            text="Are you sure you want to delete this conversation ? This action cannot be undone."
                                                            handleDel={() => dispatch(chatActions.deleteChat(chat?.chatId))}
                                                            actionType={DELETE_CHART_ACTION_TYPE}
                                                            afterSuccess={() => {
                                                                if (activeChat?.chatId === chat?.chatId) {
                                                                    dispatch(chatActions.clearActiveChat());
                                                                }
                                                            }}
                                                        />
                                                    );
                                                }}
                                            >
                                                Delete Chat
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {activeChat && <ChatPanel currentUserId={currentUserId} currentUserType={currentUserType} onBack={handleBack} />}

            <NewMessageModal
                open={newMessageOpen}
                onClose={() => setNewMessageOpen(false)}
                currentUserId={currentUserId}
                currentUserType={currentUserType}
            />
        </div>
    );
};
